"use client";

import { AskTwinMark } from "@/components/app/sessions/ask-twin-view";
import Link from "next/link";
import {
  BrainIcon,
  BookOpenIcon,
  BookTextIcon,
  ClockIcon,
  ShapesIcon,
  HelpCircleIcon,
  KeyboardIcon,
  ListChecksIcon,
  ListTodoIcon,
  MapIcon,
  MessageSquareWarningIcon,
  UserCheckIcon,
  ZapIcon,
} from "lucide-react";
import type { ReactNode, Ref } from "react";
import { Badge } from "@/components/ui/badge";

import {
  Menubar,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarMenu,
  MenubarSeparator,
  MenubarShortcut,
  MenubarTrigger,
} from "@/components/ui/menubar";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { compactInboxTime } from "@/lib/format";
import type { WorkspaceSession } from "@/lib/mlx-workspace-client";
import { cn } from "@/lib/utils";
import { TOUR_DOCS_URL } from "./tour-steps";
import { twinEntitiesHref } from "@/lib/twin-entities-href";

// The current page's item keeps the hover background: the bar says where you are, so pages need no title of their own.
export const menubarLinkClass =
  "flex items-center rounded-sm px-1.5 py-[2px] text-sm font-semibold outline-none select-none hover:bg-muted focus-visible:bg-muted aria-[current=page]:bg-muted";
const menubarTriggerCurrentClass = "data-[current]:bg-muted";

/** The menu bar's items a page can belong to. */
export type AppMenubarSection = "twin" | "agent" | "feed" | "inbox" | "channels" | "workflows" | "knowledge" | "help";

const SECTION_PATHS: ReadonlyArray<readonly [AppMenubarSection, readonly string[]]> = [
  ["twin", ["/workspace/home"]],
  ["agent", ["/workspace/sessions", "/memories", "/workspace/automations"]],
  ["feed", ["/workspace/feed"]],
  ["inbox", ["/workspace/inbox"]],
  ["channels", ["/workspace/channels"]],
  ["workflows", ["/workflows"]],
  ["knowledge", ["/knowledge", "/workspace/entities", "/authoring/skills"]],
  ["help", ["/feedback"]],
];

/** Which menu bar item a path belongs to: its own link, or the menu (Agent, Knowledge) that opens it. */
export function appMenubarSection(pathname: string): AppMenubarSection | null {
  const within = (root: string) => pathname === root || pathname.startsWith(`${root}/`);
  return SECTION_PATHS.find(([, roots]) => roots.some(within))?.[0] ?? null;
}

function sessionTitle(session: Pick<WorkspaceSession, "id" | "title">): string {
  return session.title?.trim() || session.id;
}

export type AppMenubarViewProps = {
  barRef?: Ref<HTMLElement>;
  organizationControl: ReactNode;
  userControl: ReactNode;
  /** The menu bar item the page belongs to, shown as current. */
  current?: AppMenubarSection | null;
  syncDot: { label: string; dotClassName: string };
  /** On a phone the menus collapse into this one Menu button (AppPhoneMenu). */
  phoneMenu?: ReactNode;
  newSessionHref: string;
  onOpenNewSession: () => void;
  recentSessions: Pick<WorkspaceSession, "id" | "title" | "updatedAt">[];
  showFeed: boolean;
  /** The Twin entities surface, when the deployment enables it. */
  showEntities?: boolean;
  showInbox: boolean;
  inboxBadge?: ReactNode;
  showWorkflows: boolean;
  onStartTour: () => void;
  onOpenShortcuts: () => void;
  onSendFeedback: () => void;
  /** A guest uses only their channels: the bar shows the organisation and their account. */
  guest?: boolean;
};

/**
 * The application menu bar as markup: every value it shows arrives as a prop,
 * so it renders the same with live data (AppMenubar) or fixtures (the
 * marketing product bridge). Organisation, user and phone-menu controls are
 * slots because each auth mode and screen size brings its own.
 */
export function AppMenubarView({
  barRef,
  organizationControl,
  userControl,
  current = null,
  syncDot,
  phoneMenu,
  newSessionHref,
  onOpenNewSession,
  recentSessions,
  showFeed,
  showEntities = false,
  showInbox,
  inboxBadge,
  showWorkflows,
  onStartTour,
  onOpenShortcuts,
  onSendFeedback,
  guest = false,
}: AppMenubarViewProps) {
  if (guest) {
    return (
      <header ref={barRef} className="menubar-surface sticky top-0 z-40 block">
        <div className="flex h-10 w-full items-center gap-2.5 pl-3 pr-4">
          <div className="flex min-w-0 flex-1 items-center gap-2.5">
            {organizationControl}
            <Badge variant="outline">Guest</Badge>
          </div>
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {userControl}
          </div>
        </div>
      </header>
    );
  }
  return (
    <header
      ref={barRef}
      data-tour="app-menubar"
      className="menubar-surface sticky top-0 z-40 block"
    >
      <div className="flex h-10 w-full items-center gap-2.5 pl-3 pr-4">
        {/* On a phone the menus collapse into one Menu button; the account
            menu stays pinned beside them. */}
        <div className="flex min-w-0 flex-1 items-center overflow-x-auto scrollbar-thin">
          <div className="flex min-w-max flex-1 items-center gap-2.5">
            {organizationControl}

            <div className="flex items-center gap-0.5">
              {/* Twin's home; the dot says how fresh its sources are. */}
              <Link
                href="/workspace/home"
                className={cn(menubarLinkClass, "gap-2")}
                data-tour="menu-sync"
                title={syncDot.label}
                aria-current={current === "twin" ? "page" : undefined}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    "size-2 shrink-0 rounded-full ring-2",
                    syncDot.dotClassName,
                  )}
                />
                Twin
              </Link>
              {phoneMenu}
              <Menubar className="hidden bg-transparent px-0 sm:flex">
                <MenubarMenu>
                  <MenubarTrigger data-tour="menu-agent" data-current={current === "agent" || undefined}
                    className={menubarTriggerCurrentClass}>
                    <span>Agent</span>
                  </MenubarTrigger>
                  <MenubarContent align="start" className="w-72">
                    <MenubarGroup>
                      <MenubarItem
                        render={
                          <Link
                            href={newSessionHref}
                            onClick={(event) => {
                              if (
                                event.metaKey ||
                                event.ctrlKey ||
                                event.altKey ||
                                event.shiftKey
                              ) {
                                return;
                              }
                              event.preventDefault();
                              onOpenNewSession();
                            }}
                          />
                        }
                      >
                        <AskTwinMark />
                        New session
                        <MenubarShortcut><ShortcutKeys shortcuts={["n t", "space"]} /></MenubarShortcut>
                      </MenubarItem>
                      <MenubarItem
                        render={<Link href="/workspace/sessions?assignee=me" />}
                      >
                        <UserCheckIcon data-icon="inline-start" />
                        Assigned to me
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/workspace/sessions" />}>
                        <ListTodoIcon data-icon="inline-start" />
                        All sessions
                        <MenubarShortcut><ShortcutKeys shortcuts={["g t"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/memories" />}>
                        <BrainIcon data-icon="inline-start" />
                        Memories
                      </MenubarItem>
                      {/* A tab of Workflows where Workflows shows; here only where it doesn't. */}
                      {showWorkflows ? null : (
                        <MenubarItem
                          render={<Link href="/workspace/automations" />}
                        >
                          <ZapIcon data-icon="inline-start" />
                          Scheduled questions
                        </MenubarItem>
                      )}
                    </MenubarGroup>
                    {recentSessions.length ? (
                      <>
                        <MenubarSeparator />
                        <MenubarGroup>
                          {recentSessions.map((session) => {
                            const title = sessionTitle(session);
                            return (
                              <MenubarItem
                                key={`recent-session-${session.id}`}
                                render={
                                  <Link
                                    href={`/workspace/sessions/${encodeURIComponent(session.id)}`}
                                  />
                                }
                              >
                                <ClockIcon data-icon="inline-start" />
                                <span
                                  className="max-w-[220px] truncate"
                                  title={title}
                                >
                                  {title}
                                </span>
                                {/* Two sessions can share a title; when each was last active tells them apart. */}
                                <MenubarShortcut className="pl-3 tabular-nums">
                                  {compactInboxTime(new Date(session.updatedAt).toISOString())}
                                </MenubarShortcut>
                              </MenubarItem>
                            );
                          })}
                        </MenubarGroup>
                      </>
                    ) : null}
                  </MenubarContent>
                </MenubarMenu>

                {showFeed ? (
                  <Link
                    href="/workspace/feed"
                    className={cn(menubarLinkClass, "gap-1")}
                    aria-current={current === "feed" ? "page" : undefined}
                    data-tour="menu-feed"
                  >
                    Feed
                  </Link>
                ) : null}
                {showInbox ? (
                  <Link
                    href="/workspace/inbox"
                    className={cn(menubarLinkClass, "gap-1")}
                    aria-current={current === "inbox" ? "page" : undefined}
                    data-tour="menu-inbox"
                  >
                    Inbox
                    {inboxBadge}
                  </Link>
                ) : null}
                <Link
                  href="/workspace/channels"
                  className={menubarLinkClass}
                  aria-current={current === "channels" ? "page" : undefined}
                  data-tour="menu-channels"
                >
                  Channels
                </Link>
                {showWorkflows ? (
                  <Link
                    href="/workflows"
                    className={menubarLinkClass}
                    aria-current={current === "workflows" ? "page" : undefined}
                    data-tour="menu-workflows"
                  >
                    Workflows
                  </Link>
                ) : null}
                <MenubarMenu>
                  <MenubarTrigger data-tour="menu-knowledge" data-current={current === "knowledge" || undefined}
                    className={menubarTriggerCurrentClass}>
                    Knowledge
                  </MenubarTrigger>
                  <MenubarContent align="start" className="w-64">
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/knowledge" />}>
                        <BookOpenIcon data-icon="inline-start" />
                        Knowledge
                        <MenubarShortcut><ShortcutKeys shortcuts={["g d"]} /></MenubarShortcut>
                      </MenubarItem>
                      {showEntities ? (
                        <MenubarItem render={<Link href={twinEntitiesHref()} />}>
                          <ShapesIcon data-icon="inline-start" />
                          Entities
                        </MenubarItem>
                      ) : null}
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem render={<Link href="/authoring/skills" />}>
                        <BookTextIcon data-icon="inline-start" />
                        Skills
                        <MenubarShortcut><ShortcutKeys shortcuts={["g k"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                  </MenubarContent>
                </MenubarMenu>

                <MenubarMenu>
                  <MenubarTrigger data-current={current === "help" || undefined} className={menubarTriggerCurrentClass}>
                    Help
                  </MenubarTrigger>
                  <MenubarContent align="start" className="w-64">
                    <MenubarGroup>
                      <MenubarItem onClick={onStartTour}>
                        <MapIcon data-icon="inline-start" />
                        Product tour
                      </MenubarItem>
                      {/* The tour's last stop points here for the rest. */}
                      <MenubarItem render={<a href={TOUR_DOCS_URL} target="_blank" rel="noreferrer" />}>
                        <BookOpenIcon data-icon="inline-start" />
                        Workspace guide
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem onClick={onOpenShortcuts}>
                        <KeyboardIcon data-icon="inline-start" />
                        Keyboard shortcuts
                        <MenubarShortcut><ShortcutKeys shortcuts={["$mod+/"]} /></MenubarShortcut>
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem onClick={onSendFeedback}>
                        <MessageSquareWarningIcon data-icon="inline-start" />
                        Send feedback
                      </MenubarItem>
                      <MenubarItem render={<Link href="/feedback" />}>
                        <ListChecksIcon data-icon="inline-start" />
                        View feedback
                      </MenubarItem>
                    </MenubarGroup>
                    <MenubarSeparator />
                    <MenubarGroup>
                      <MenubarItem
                        render={
                          <a href="mailto:support@mercurylabs.io?subject=Twin%20support" />
                        }
                      >
                        <HelpCircleIcon data-icon="inline-start" />
                        Support
                      </MenubarItem>
                    </MenubarGroup>
                  </MenubarContent>
                </MenubarMenu>
              </Menubar>
            </div>
          </div>
        </div>
        <div className="ml-auto flex shrink-0 items-center gap-2">
          {userControl}
        </div>
      </div>
    </header>
  );
}
